import React, { ChangeEvent, ReactNode, useEffect, useState } from "react";
import classNames from "classnames";

import { Themes } from "custom-types/Themes";

import FormElementErrorMessage from "components/botanic/FormElementErrorMessage";
import FormElementLabel from "components/botanic/FormElementLabel";
import FormField from "components/FormField";

type SelectProps = {
  activeOnLoad?: boolean;
  children: ReactNode;
  className?: string;
  dynamicLabel?: boolean;
  errorMessage?: string;
  helperText?: string;
  id: string;
  label?: string;
  onBlur?: () => void;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  theme?: keyof typeof Themes;
} & React.SelectHTMLAttributes<HTMLSelectElement>;

const Select: React.FC<SelectProps> = ({
  activeOnLoad = true,
  children,
  className,
  dynamicLabel = false,
  errorMessage,
  helperText,
  id,
  label,
  onBlur = () => {},
  onChange = () => {},
  theme,
  ...others
}) => {
  const selectRef = React.useRef<HTMLSelectElement>(null);

  const [isActive, _setIsActive] = useState(!dynamicLabel || activeOnLoad);
  const setIsActive = (bool: boolean) => dynamicLabel && _setIsActive(bool);

  useEffect(() => {
    setIsActive(!!selectRef.current?.value);
  }, []);

  const classes = classNames(
    {
      "form--light": theme === Themes.light,
      form__field: !isActive,
      "form__field--active": isActive,
    },
    className
  );

  return (
    <FormField className={classes}>
      {label ? (
        <FormElementLabel
          hasError={!!errorMessage}
          htmlFor={id}
          label={label}
        />
      ) : (
        <></>
      )}
      <select
        id={id}
        ref={selectRef}
        className={classNames({ unlabeled: !label })}
        onChange={(e) => {
          onChange(e);
          setIsActive(!!selectRef.current?.value);
        }}
        onBlur={(e) => {
          onBlur(e);
          setIsActive(!!selectRef.current?.value);
        }}
        {...others}
      >
        {children}
      </select>
      {helperText && !errorMessage ? <p>{helperText}</p> : <></>}
      {errorMessage ? (
        <FormElementErrorMessage message={errorMessage} />
      ) : (
        <></>
      )}
    </FormField>
  );
};

export default Select;
